import PropTypes from "prop-types";
import { Icon } from "@iconify/react";
import menu2Fill from "@iconify/icons-eva/menu-2-fill";
import { styled } from "@mui/material/styles";
import {
  Box,
  Stack,
  AppBar,
  Toolbar,
  IconButton,
  Typography,
} from "@mui/material";
import { MHidden } from "../../components/@material-extend";
import AccountPopover from "./AccountPopover";
import NotificationsPopover from "./NotificationsPopover";
import { get_root_value } from "src/utils/domUtils";
import { useEffect, useState } from "react";
import {
  markNotificationAsRead,
  notificationList,
} from "src/DAL/Notification/Notification";
import { useSnackbar } from "notistack";
import { useNavigate } from "react-router-dom";
import ChatPopover from "src/components/ChatPopover";
import { useContentSetting } from "src/Hooks/ContentSettingState";

const DRAWER_WIDTH = 280;
const APPBAR_MOBILE = 64;
const APPBAR_DESKTOP = 42;

const RootStyle = styled(AppBar)(({ theme }) => ({
  boxShadow: "none",
  backdropFilter: "blur(6px)",
  WebkitBackdropFilter: "blur(6px)", // Fix on Mobile
  backgroundColor: get_root_value("--background-primary-color"),
  [theme.breakpoints.up("lg")]: {
    width: `calc(100% - ${DRAWER_WIDTH + 1}px)`,
  },
}));

const ToolbarStyle = styled(Toolbar)(({ theme }) => ({
  minHeight: APPBAR_MOBILE,
  [theme.breakpoints.up("lg")]: {
    minHeight: APPBAR_DESKTOP + 20,
    padding: theme.spacing(0, 3),
  },
}));

DashboardNavbar.propTypes = {
  onOpenSidebar: PropTypes.func,
};

export default function DashboardNavbar({ onOpenSidebar }) {
  const navigate = useNavigate();
  const [allNotifications, setAllNotifications] = useState([]);
  const [totalNotifications, setTotalNotifications] = useState(0);
  const [unreadNotificationCount, setUnreadNotificationCount] = useState(0);
  const [open, setOpen] = useState(false);
  const {
    handleUnreadMessageCount,
    pageTitle,
    showNavBackButton,
    navbarSubtitle,
  } = useContentSetting();

  const { enqueueSnackbar } = useSnackbar();
  const handleRedirect = async (
    notification_id,
    notification_type,
    notification,
  ) => {
    setOpen(false);
    const result = await markNotificationAsRead(notification_id);

    if (result.code === 200) {
      getNotificationList();
      setAllNotifications((notifications) => {
        const notification_list = notifications.map((notification) => {
          if (notification._id === notification_id) {
            return {
              ...notification,
              is_read: true,
            };
          } else {
            return notification;
          }
        });
        return notification_list;
      });
      if (notification_type === "member_left_group") {
        navigate(`/group-feed/${notification.action_id}`);
      } else if (notification_type === "feed_added") {
        navigate(`/community-area-post-detail/${notification.action_id}`);
      } else if (notification_type === "feed_like") {
        navigate(`/community-area-post-detail/${notification.action_id}`);
      } else if (notification_type === "feed_comment_add") {
        navigate(`/community-area-post-detail/${notification.action_id}`);
      } else if (notification_type === "feed_pending") {
        navigate(`/pending-posts`);
      } else if (notification_type === "feed_approved") {
        navigate(`/community-area`);
      } else if (notification_type === "feed_rejected") {
        navigate(`/rejected-posts`);
      } else if (notification_type === "member_removed_from_group") {
        navigate(`/group-feed/${notification.action_id}`);
      } else if (notification_type === "member_added_to_group") {
        navigate(`/group-feed/${notification.action_id}`);
      } else if (notification_type === "form_completion_send") {
        navigate(`/coach_forms`);
      } else if (notification_type === "add_support_ticket") {
        navigate(`/support-ticket`);
      } else if (notification_type === "form_feedback") {
        navigate(
          `/coach_forms-Details/${notification.form_slug}/${userInfo._id}`,
        );
      } else if (notification_type === "form_feedback_edit") {
        navigate(
          `/coach_forms-Details/${notification.form_slug}/${userInfo._id}`,
        );
      } else if (notification_type === "workout_comment_added") {
        const memberId = notification.member;
        const programId = notification.program;
        const workoutId = notification.workout || notification.action_id;
        const workoutDate = notification.workout_date;

        if (!memberId || !programId || !workoutId) {
          enqueueSnackbar("Invalid notification data", { variant: "error" });
          return;
        }
        const dateQuery = workoutDate
          ? `?date=${encodeURIComponent(workoutDate)}`
          : "";

        navigate(
          `/member/${memberId}/workout-edit/${programId}/${workoutId}${dateQuery}`,
        );
      } else if (notification_type === "event_reminder") {
        navigate(`/calendar-events`);
      }
    } else {
      enqueueSnackbar(result.message, { variant: "error" });
    }
  };

  const getNotificationList = async () => {
    const result = await notificationList();
    if (result.code === 200) {
      handleUnreadMessageCount(result?.unread_chat_counr);
      setAllNotifications(result.notifications);

      setTotalNotifications(result.total_notifications_count);
      setUnreadNotificationCount(result.unread_notifications);
    } else {
      enqueueSnackbar(result.message, { variant: "error" });
    }
  };

  useEffect(() => {
    getNotificationList();
  }, []);

  return (
    <RootStyle>
      <ToolbarStyle>
        <MHidden width="lgUp">
          <IconButton
            onClick={onOpenSidebar}
            sx={{ mr: 1, color: "text.primary", marginRight: "auto" }}
          >
            <Icon icon={menu2Fill} />
          </IconButton>
        </MHidden>

        {pageTitle ? (
          <Stack
            direction="row"
            alignItems="center"
            spacing={1}
            sx={{
              minHeight: 48,
              flex: 1,
              minWidth: 0,
              overflow: "hidden",
              mr: 1,
            }}
          >
            {showNavBackButton && (
              <IconButton
                size="small"
                onClick={() => navigate(-1)}
                sx={{
                  color: "primary.main",
                  p: 0.5,
                  flexShrink: 0,
                }}
              >
                <Icon icon="eva:arrow-back-fill" width={20} height={20} />
              </IconButton>
            )}

            <Box sx={{ overflow: "hidden", minWidth: 0 }}>
              <Typography
                noWrap
                sx={{
                  color: "primary.main",
                  fontWeight: 700,
                  fontSize: { xs: "13px", sm: "18px" },
                  lineHeight: 1.3,
                  mb: 0.25,
                }}
              >
                {pageTitle}
              </Typography>

              {navbarSubtitle && (
                <Typography
                  noWrap
                  sx={{
                    color: "primary.main",
                    opacity: 0.75,
                    fontSize: { xs: "11px", sm: "13px" },
                    lineHeight: 1.2,
                  }}
                >
                  {navbarSubtitle}
                </Typography>
              )}
            </Box>
          </Stack>
        ) : null}
        {!pageTitle && <Box sx={{ flexGrow: 1 }} />}

        <Stack
          direction="row"
          alignItems="center"
          spacing={{ xs: 0.5, sm: 0.7 }}
        >
          <ChatPopover />
          <NotificationsPopover
            totalNotifications={totalNotifications}
            allNotifications={allNotifications}
            unreadNotificationCount={unreadNotificationCount}
            setUnreadNotificationCount={setUnreadNotificationCount}
            setAllNotifications={setAllNotifications}
            handleRedirect={handleRedirect}
            setOpen={setOpen}
            open={open}
          />
          <AccountPopover />
        </Stack>
      </ToolbarStyle>
    </RootStyle>
  );
}
